import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnChanges,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { SprintService } from 'src/app/private/services/sprint.service';
import { UserStoryService } from 'src/app/private/services/user-story.service';
import { ProjectTaskService } from 'src/app/private/services/project-task.service';
import { BugService } from 'src/app/private/services/bug.service';
import { ProjectFeatureService } from 'src/app/private/services/project-feature.service';
import { EpicService } from 'src/app/private/services/epic.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-project-detail-sprint',
  templateUrl: './project-detail-sprint.component.html',
  styleUrls: ['./project-detail-sprint.component.scss'],
})
export class ProjectDetailSprintComponent implements OnInit {
  @ViewChild('sprintModal') sprintModal: any;

  openCreateModal(sprint?: any) {
    this.openSprintModal(this.sprintModal, sprint);
  }
  @Input() projectId!: string;
  @Input() projectMembers: any[] = [];
  @Input() currentUserId: any;
  @Input() sprints: any[] = [];
  @Input() projectStories: any[] = [];
  @Input() loadingSprints = false;
  @Input() loadingProjectStories = false;

  @Output() taskSelected = new EventEmitter<any>();
  @Output() taskEdit = new EventEmitter<any>();
  @Output() addTaskClicked = new EventEmitter<any>();
  @Output() bugEdit = new EventEmitter<any>();
  @Output() storyEdit = new EventEmitter<any>();
  @Output() sprintsUpdated = new EventEmitter<void>();
  @Output() storiesUpdated = new EventEmitter<void>();

  private _selectedSprint: any = null;
  @Input()
  set selectedSprint(val: any) {
    if (this._selectedSprint !== val) {
      this._selectedSprint = val;
      this.selectedSprintChange.emit(val);
      this.recomputeLists();
    }
  }
  get selectedSprint(): any {
    return this._selectedSprint;
  }
  @Output() selectedSprintChange = new EventEmitter<any>();
  activeSprintTab = 'backlog';
  sprintFormAction = 'Add';
  currentSprintId: any = null;
  btnLoading = false;

  // Board columns matching reference design
  boardColumns = [
    { key: 'todo', label: 'To Do', color: '#3b82f6' },
    { key: 'in_progress', label: 'In Progress', color: '#6366f1' },
    { key: 'code_review', label: 'Code Review', color: '#f59e0b' },
    { key: 'testing', label: 'Testing', color: '#8b5cf6' },
    { key: 'done', label: 'Done', color: '#22c55e' },
  ];

  isAdmin = false;
  onlyMyTasks = false;
  onlyMyBugs = false;

  // Task table server-side pagination
  taskFilterStatus = '';
  taskFilterAssignee = '';
  taskPage = 1;
  taskPageSize = 5;
  taskTotal = 0;
  taskLoading = false;

  // Bug table server-side pagination
  bugFilterStatus = '';
  bugFilterAssignee = '';
  bugPage = 1;
  bugPageSize = 5;
  bugTotal = 0;
  bugLoading = false;

  // Backlog load-more pagination
  backlogPage = 1;
  backlogPageSize = 20;
  backlogTotal = 0;
  backlogHasMore = false;
  backlogLoadingMore = false;

  // Sprint backlog search & data models
  storySearchQuery = '';
  searchBacklogQuery = '';
  selectedBacklogStoryIds: { [key: number]: boolean } = {};
  projectFeatures: any[] = [];

  newStoryData: any = {
    title: '',
    description: '',
    story_points: 1,
    priority: 'medium',
    status: 'todo',
    feature_id: null,
    sprint_id: null,
    client_id: '',
  };

  csvText = '';
  showAddStoriesDropdown = false;

  sprintData: any = {
    name: '',
    description: '',
    goal: '',
    start_date: '',
    end_date: '',
    team: '',
    velocity: null,
    capacity: null,
    estimated_story_points: null,
    status: 'planning',
    project_id: '',
    client_id: '',
  };

  constructor(
    private modalService: NgbModal,
    private toastr: ToastrService,
    private localStorage: LocalStorageService,
    private sprintService: SprintService,
    private userStoryService: UserStoryService,
    private taskService: ProjectTaskService,
    private bugService: BugService,
    private featureService: ProjectFeatureService,
    private epicService: EpicService,
  ) {}

  ngOnInit() {
    this.isAdmin = this.localStorage.checkFullAccessForTableList(
      this.localStorage.getRole(),
    );
    this.sprintData.project_id = this.projectId;
    this.sprintData.client_id = this.localStorage.getClientId();
    this.loadProjectFeatures();

    // Auto-select active or first sprint if none is selected
    if (this.sprints && this.sprints.length > 0 && !this.selectedSprint) {
      this.selectedSprint =
        this.sprints.find((s) => s.status === 'active') || this.sprints[0];
    }
  }

  ngOnChanges() {
    this.loadProjectFeatures();
    if (this.sprints && this.sprints.length > 0 && !this.selectedSprint) {
      this.selectedSprint =
        this.sprints.find((s) => s.status === 'active') || this.sprints[0];
    }
    this.recomputeLists();
  }

  // ── Cached computed lists ──────────────────────────────────────────────
  private _enrichedStories: any[] = [];
  sprintStoriesList: any[] = [];
  activeSprintStories: any[] = [];
  filteredSprintStories: any[] = [];
  backlogStories: any[] = [];
  filteredBacklogStories: any[] = [];
  // API-fetched sprint tasks/bugs
  pagedSprintTasks: any[] = [];
  taskTotalPages = 1;
  taskPageNumbers: number[] = [];
  pagedSprintBugs: any[] = [];
  bugTotalPages = 1;
  bugPageNumbers: number[] = [];
  activeSprint: any = null;
  // Legacy lists for board view (still uses enriched stories fallback)
  sprintTasks: any[] = [];
  sprintBugs: any[] = [];

  recomputeLists() {
    // 1. Build feature lookup map for epic/feature name enrichment
    const featureMap = new Map<number, { feature_name: string; epic_name: string }>();
    (this.projectFeatures || []).forEach((feat: any) => {
      featureMap.set(Number(feat.feature_id), {
        feature_name: feat.name || feat.feature_name || '',
        epic_name: feat.epic_name || '',
      });
    });

    // 2. Build base story list (from projectStories or hierarchy fallback)
    let baseList: any[];
    if (this.projectStories && this.projectStories.length > 0) {
      baseList = this.projectStories;
    } else {
      // hierarchy fallback — build without mutating originals
      const hierarchyStories: any[] = [];
      (this.projectFeatures || []).forEach((feat: any) => {
        if (feat.stories) {
          feat.stories.forEach((story: any) => {
            hierarchyStories.push({
              ...story,
              epic_name: feat.epic_name,
              feature_name: feat.name,
            });
          });
        }
      });
      baseList = hierarchyStories;
    }

    // 3. Enrich stories (only spread when enrichment needed, avoiding new refs otherwise)
    this._enrichedStories = baseList.map((s: any) => {
      if (s.feature_id && featureMap.has(Number(s.feature_id))) {
        const info = featureMap.get(Number(s.feature_id))!;
        if (!s.feature_name || !s.epic_name) {
          return {
            ...s,
            feature_name: s.feature_name || info.feature_name,
            epic_name: s.epic_name || info.epic_name,
          };
        }
      }
      return s;
    });
    this.sprintStoriesList = this._enrichedStories;

    // 4. Active sprint stories
    if (this.selectedSprint) {
      this.activeSprintStories = this._enrichedStories.filter(
        (s) => Number(s.sprint_id) === Number(this.selectedSprint.sprint_id),
      );
    } else {
      this.activeSprintStories = [];
    }

    // 5. Backlog stories
    this.backlogStories = this._enrichedStories.filter((s) => !s.sprint_id);

    // 6. Filtered sprint stories
    this.recomputeFilteredStories();

    // 7. Filtered backlog stories
    this.recomputeFilteredBacklog();

    // 8. Tasks and bugs
    const fallbackTasks: any[] = [];
    this._enrichedStories.forEach((story: any) => {
      if (story.tasks) {
        story.tasks.forEach((task: any) => {
          fallbackTasks.push({ ...task, user_story_id: story.user_story_id, sprint_id: story.sprint_id });
        });
      }
    });
    const fallbackBugs: any[] = [];
    this._enrichedStories.forEach((story: any) => {
      if (story.bugs) {
        story.bugs.forEach((bug: any) => {
          fallbackBugs.push({ ...bug, user_story_id: story.user_story_id, sprint_id: story.sprint_id });
        });
      }
    });

    // 8b. Build sprintTasks/sprintBugs for board view from hierarchy fallback only
    if (this.selectedSprint) {
      const sprintId = Number(this.selectedSprint.sprint_id);
      this.sprintTasks = fallbackTasks.filter((t) => t.sprint_id && Number(t.sprint_id) === sprintId);
      this.sprintBugs = fallbackBugs.filter((b) => b.sprint_id && Number(b.sprint_id) === sprintId);
    } else {
      this.sprintTasks = [];
      this.sprintBugs = [];
    }

    // 9. Trigger server-side tasks & bugs load for table views
    this.loadSprintTasks();
    this.loadSprintBugs();

    // 10. Active sprint
    this.activeSprint = (this.sprints || []).find((s) => s.status === 'active') || null;
  }

  recomputeFilteredStories() {
    if (this.storySearchQuery) {
      const q = this.storySearchQuery.toLowerCase();
      this.filteredSprintStories = this.activeSprintStories.filter(
        (s) =>
          (s.title || '').toLowerCase().includes(q) ||
          (s.story_key || '').toLowerCase().includes(q),
      );
    } else {
      this.filteredSprintStories = [...this.activeSprintStories];
    }
  }

  recomputeFilteredBacklog() {
    if (this.searchBacklogQuery) {
      const q = this.searchBacklogQuery.toLowerCase();
      this.filteredBacklogStories = this.backlogStories.filter(
        (s) =>
          (s.title || '').toLowerCase().includes(q) ||
          (s.story_key || '').toLowerCase().includes(q),
      );
    } else {
      this.filteredBacklogStories = [...this.backlogStories];
    }
  }

  // ── Server-side task pagination ──────────────────────────────────────────
  recomputeFilteredTasks() {
    this.loadSprintTasks();
  }

  loadSprintTasks() {
    if (!this.selectedSprint) return;
    this.taskLoading = true;
    const sprintId = this.selectedSprint.sprint_id;
    const whereClause: any = {
      project_id: this.projectId,
      client_id: this.localStorage.getClientId(),
    };
    if (this.taskFilterStatus) whereClause.status = this.taskFilterStatus;
    if (this.taskFilterAssignee) whereClause.assignee_id = this.taskFilterAssignee;
    if (this.onlyMyTasks) whereClause.only_my_tasks = true;

    this.taskService.getAllProjectTasks({
      user_id: this.localStorage.getUserId(),
      client_id: this.localStorage.getClientId(),
      sprint_id: sprintId,
      page: this.taskPage,
      limit: this.taskPageSize,
      where: whereClause,
      order: { col: 'updated_at', dir: 'DESC' },
    }).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        const rows = data?.rows || data?.tasks || [];
        this.taskTotal = data?.total ?? data?.count ?? data?.totalCount ?? rows.length;

        if (this.onlyMyTasks && this.localStorage.getUserId()) {
          const uid = Number(this.localStorage.getUserId());
          this.pagedSprintTasks = (rows || []).filter((t: any) => {
            const isAssigned = Number(t.assignee_id) === uid ||
              (Array.isArray(t.assignee_ids) && t.assignee_ids.map(Number).includes(uid)) ||
              (t.assignees && t.assignees.some((a: any) => Number(a.user_id) === uid));
            const isCreated = Number(t.created_by) === uid || Number(t.reporter_id) === uid;
            return isAssigned || isCreated;
          });
        } else {
          this.pagedSprintTasks = rows;
        }

        this.taskTotalPages = Math.max(1, Math.ceil(this.taskTotal / this.taskPageSize));
        this.taskPageNumbers = Array.from({ length: Math.min(this.taskTotalPages, 10) }, (_, i) => i + 1);
        this.taskLoading = false;
      },
      error: () => {
        this.taskLoading = false;
        this.toastr.error('Failed to load tasks');
      }
    });
  }

  // ── Server-side bug pagination ──────────────────────────────────────────
  recomputeFilteredBugs() {
    this.loadSprintBugs();
  }

  loadSprintBugs() {
    if (!this.selectedSprint) return;
    this.bugLoading = true;
    const sprintId = this.selectedSprint.sprint_id;
    const whereClause: any = {
      project_id: this.projectId,
      client_id: this.localStorage.getClientId(),
    };
    if (this.bugFilterStatus) whereClause.status = this.bugFilterStatus;
    if (this.bugFilterAssignee) whereClause.assigned_to = this.bugFilterAssignee;
    if (this.onlyMyBugs) whereClause.only_my_bugs = true;

    this.bugService.getAllBugs({
      user_id: this.localStorage.getUserId(),
      client_id: this.localStorage.getClientId(),
      sprint_id: sprintId,
      page: (this.bugPage - 1) * this.bugPageSize,
      limit: this.bugPageSize,
      where: whereClause,
      order: { col: '"bug"."updated_at"', dir: 'DESC' },
    }).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        const rows = data?.rows || data?.bugs || [];
        this.bugTotal = data?.count ?? data?.total ?? data?.totalCount ?? rows.length;

        if (this.onlyMyBugs && this.localStorage.getUserId()) {
          const uid = Number(this.localStorage.getUserId());
          this.pagedSprintBugs = (rows || []).filter((b: any) => {
            const isAssigned = Number(b.assigned_to) === uid ||
              Number(b.tester_id) === uid ||
              (Array.isArray(b.assigned_to_ids) && b.assigned_to_ids.map(Number).includes(uid));
            const isCreated = Number(b.created_by) === uid || Number(b.reported_by) === uid;
            return isAssigned || isCreated;
          });
        } else {
          this.pagedSprintBugs = rows;
        }

        this.bugTotalPages = Math.max(1, Math.ceil(this.bugTotal / this.bugPageSize));
        this.bugPageNumbers = Array.from({ length: Math.min(this.bugTotalPages, 10) }, (_, i) => i + 1);
        this.bugLoading = false;
      },
      error: () => {
        this.bugLoading = false;
        this.toastr.error('Failed to load bugs');
      }
    });
  }

  toggleMyTasks() {
    this.onlyMyTasks = !this.onlyMyTasks;
    this.taskPage = 1;
    this.loadSprintTasks();
  }

  toggleMyBugs() {
    this.onlyMyBugs = !this.onlyMyBugs;
    this.bugPage = 1;
    this.loadSprintBugs();
  }

  normalizeStatus(status: string): string {
    if (!status) return 'todo';
    const s = status.toLowerCase().replace(/_/g, '').replace(/-/g, '').trim();
    if (s === 'todo' || s === 'notstarted' || s === 'open' || s === 'assigned')
      return 'todo';
    if (s === 'inprogress') return 'in_progress';
    if (s === 'codereview' || s === 'inreview') return 'code_review';
    if (s === 'testing' || s === 'readyfortest') return 'testing';
    if (s === 'done' || s === 'completed' || s === 'closed' || s === 'resolved')
      return 'done';
    return 'todo';
  }

  getSprintTasksByStatus(status: string): any[] {
    const tasks = this.sprintTasks.filter(
      (t) => this.normalizeStatus(t.status || t.project_task_status) === status,
    );
    const bugs = this.sprintBugs.filter(
      (b) => this.normalizeStatus(b.status || b.bug_status) === status,
    );
    return [...tasks, ...bugs];
  }

  getSprintTasksPointsByStatus(status: string): number {
    return this.getSprintTasksByStatus(status).reduce(
      (sum, item) =>
        sum +
        Number(item.story_points || item.points || item.estimated_hours || 0),
      0,
    );
  }

  onTaskFilterChange() {
    this.taskPage = 1;
    this.loadSprintTasks();
  }

  onBugFilterChange() {
    this.bugPage = 1;
    this.loadSprintBugs();
  }

  getTimeAgo(dateStr: string): string {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  getSprintStoryPoints(sprint: any): {
    completed: number;
    total: number;
    pct: number;
  } {
    if (!sprint) return { completed: 0, total: 0, pct: 0 };
    const sid = Number(sprint.sprint_id);

    const stories = (this.projectStories || []).filter(
      (s: any) => Number(s.sprint_id) === sid
    );
    const storyIdsInSprint = new Set<number>(
      stories.map((s: any) => Number(s.user_story_id)).filter(Boolean)
    );

    const tasksInSprint = (this.sprintTasks || []).filter((t: any) => {
      if (Number(t.sprint_id) === sid) return true;
      if (t.user_story_id && storyIdsInSprint.has(Number(t.user_story_id))) return true;
      return false;
    });
    const completedTasks = tasksInSprint.filter((t: any) => {
      const s = String(t.status || t.project_task_status || '').toLowerCase().replace(/[\s_-]+/g, '');
      return s === 'completed' || s === 'done' || s === 'closed' || s === 'resolved';
    }).length;

    const bugsInSprint = (this.sprintBugs || []).filter((b: any) => {
      if (Number(b.sprint_id) === sid) return true;
      if (b.user_story_id && storyIdsInSprint.has(Number(b.user_story_id))) return true;
      return false;
    });
    const closedBugs = bugsInSprint.filter((b: any) => {
      const s = String(b.status || '').toLowerCase().replace(/[\s_-]+/g, '');
      return s === 'closed' || s === 'resolved' || s === 'rejected' || s === 'completed';
    }).length;

    let total = tasksInSprint.length + bugsInSprint.length;
    let completed = completedTasks + closedBugs;

    if (total === 0 && stories.length > 0) {
      total = stories.length;
      completed = stories.filter((s: any) => {
        const st = String(s.status || '').toLowerCase().replace(/[\s_-]+/g, '');
        return st === 'completed' || st === 'closed';
      }).length;
    }

    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, pct };
  }


  moveStoryToSprint(story: any, sprintId: any) {
    const payload = {
      user_story_id: story.user_story_id,
      client_id: this.localStorage.getClientId(),
      sprint_id: sprintId ? Number(sprintId) : null,
    };
    this.userStoryService.updateUserStory(payload).subscribe({
      next: () => {
        this.toastr.success('Story moved successfully');
        this.storiesUpdated.emit();
      },
      error: () => {
        this.toastr.error('Failed to move story');
      },
    });
  }

  deleteSprintStory(story: any) {
    if (!confirm(`Remove "${story.title}" from this sprint?`)) return;
    const payload = {
      user_story_id: story.user_story_id,
      client_id: this.localStorage.getClientId(),
      sprint_id: null,
    };
    this.userStoryService.updateUserStory(payload).subscribe({
      next: () => {
        this.toastr.success('Story removed from sprint');
        this.storiesUpdated.emit();
      },
      error: () => {
        this.toastr.error('Failed to remove story from sprint');
      },
    });
  }

  openSprintModal(content: any, sprint?: any) {
    this.sprintFormAction = sprint ? 'Update' : 'Add';
    this.currentSprintId = sprint?.sprint_id || null;
    if (sprint) {
      this.sprintData = { ...sprint };
      this.sprintData.status = (sprint.status || 'planning').toLowerCase();
      if (sprint.start_date)
        this.sprintData.start_date = new Date(sprint.start_date)
          .toISOString()
          .split('T')[0];
      if (sprint.end_date)
        this.sprintData.end_date = new Date(sprint.end_date)
          .toISOString()
          .split('T')[0];
    } else {
      this.sprintData = {
        name: '',
        description: '',
        goal: '',
        start_date: '',
        end_date: '',
        team: '',
        velocity: null,
        capacity: null,
        estimated_story_points: null,
        status: 'planning',
        project_id: this.projectId,
        client_id: this.localStorage.getClientId(),
      };
    }
    this.modalService.open(content, { centered: true, size: 'md' });
  }

  submitSprint() {
    this.btnLoading = true;
    this.sprintData.status = (
      this.sprintData.status || 'planning'
    ).toLowerCase();
    const req =
      this.sprintFormAction === 'Add'
        ? this.sprintService.createSprint(this.sprintData)
        : this.sprintService.updateSprint(this.sprintData);

    req.subscribe({
      next: (res: any) => {
        this.toastr.success(
          `Sprint ${this.sprintFormAction === 'Add' ? 'created' : 'updated'} successfully`,
        );
        this.sprintsUpdated.emit();
        if (
          this.sprintFormAction === 'Update' &&
          this.selectedSprint?.sprint_id === this.sprintData.sprint_id
        ) {
          this.selectedSprint = { ...this.selectedSprint, ...this.sprintData };
        }
        this.modalService.dismissAll();
        this.btnLoading = false;
      },
      error: (e: any) => {
        this.toastr.error(e?.error?.message || 'Failed to save sprint');
        this.btnLoading = false;
      },
    });
  }

  startSprint(sprint: any) {
    // Enforce one-active-sprint rule: check if any OTHER sprint is already active
    const currentlyActive = (this.sprints || []).find(
      (s) => s.status === 'active' && Number(s.sprint_id) !== Number(sprint.sprint_id),
    );
    if (currentlyActive) {
      this.toastr.warning(
        `"${currentlyActive.name}" is already active. Complete or close it before starting a new sprint.`,
        'Only One Active Sprint Allowed',
        { timeOut: 5000 },
      );
      return;
    }

    if (!confirm(`Start Sprint: ${sprint.name}? This will make it active.`))
      return;
    const payload = {
      sprint_id: sprint.sprint_id,
      client_id: this.localStorage.getClientId(),
      status: 'active',
      start_date: new Date().toISOString().split('T')[0],
    };
    this.sprintService.updateSprint(payload).subscribe({
      next: () => {
        this.toastr.success('Sprint started successfully');
        this.sprintsUpdated.emit();
        if (this.selectedSprint?.sprint_id === sprint.sprint_id) {
          this.selectedSprint.status = 'active';
        }
      },
      error: (e: any) => {
        this.toastr.error(e?.error?.message || 'Failed to start sprint');
      },
    });
  }

  completeSprint(sprint: any) {
    if (
      !confirm(
        `Complete Sprint: ${sprint.name}? This will close all completed tasks.`,
      )
    )
      return;
    const payload = {
      sprint_id: sprint.sprint_id,
      client_id: this.localStorage.getClientId(),
      status: 'completed',
      end_date: new Date().toISOString().split('T')[0],
    };
    this.sprintService.updateSprint(payload).subscribe({
      next: () => {
        this.toastr.success('Sprint completed successfully');
        this.sprintsUpdated.emit();
        if (this.selectedSprint?.sprint_id === sprint.sprint_id) {
          this.selectedSprint.status = 'completed';
        }
      },
      error: (e: any) => {
        this.toastr.error(e?.error?.message || 'Failed to complete sprint');
      },
    });
  }

  deleteSprint(sprint: any, event: any) {
    event.stopPropagation();
    if (!confirm(`Are you sure you want to delete ${sprint.name}?`)) return;
    this.sprintService.deleteSprint(sprint.sprint_id).subscribe({
      next: () => {
        this.toastr.success('Sprint deleted successfully');
        this.sprintsUpdated.emit();
        if (this.selectedSprint?.sprint_id === sprint.sprint_id) {
          this.selectedSprint = null;
        }
      },
      error: (e: any) => {
        this.toastr.error(e?.error?.message || 'Failed to delete sprint');
      },
    });
  }

  prettyLabel(status: string): string {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  getSeverityClass(severity: string): string {
    const classes: any = {
      low: 'severity-low',
      medium: 'severity-medium',
      high: 'severity-high',
      critical: 'severity-critical',
    };
    return classes[severity?.toLowerCase()] || '';
  }

  getTaskCountForMember(userId: any): number {
    return (this.sprintTasks || []).filter(
      (t: any) => Number(t.assignee_id) === Number(userId),
    ).length;
  }

  getDayCount(start: string, end: string): number {
    if (!start || !end) return 0;
    const ms = new Date(end).getTime() - new Date(start).getTime();
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  }

  getInitials(user: any): string {
    if (!user) return '?';
    const f = (user.fname || user.prefix || '')[0] || '';
    const l = (user.lname || '')[0] || '';
    return (f + l).toUpperCase() || '?';
  }

  loadProjectFeatures() {
    this.epicService
      .getProjectHierarchy({
        project_id: this.projectId,
        client_id: this.localStorage.getClientId(),
      })
      .subscribe({
        next: (res: any) => {
          const epics = res?.rows || res?.data?.rows || res?.data || res || [];
          const features: any[] = [];
          epics.forEach((epic: any) => {
            if (epic.features) {
              features.push(...epic.features);
            }
          });
          this.projectFeatures = features;
          this.recomputeLists();
        },
      });
  }

  openAddStoriesModal(content: any) {
    this.selectedBacklogStoryIds = {};
    this.searchBacklogQuery = '';
    // Reset and fetch from API
    this.backlogPage = 1;
    this.filteredBacklogStories = [];
    this.backlogHasMore = false;
    this.modalService.open(content, { centered: true, size: 'lg' });
    this.loadBacklogStories();
  }

  loadBacklogStories() {
    this.backlogLoadingMore = true;
    this.userStoryService.getAllUserStories({
      user_id: this.localStorage.getUserId(),
      client_id: this.localStorage.getClientId(),
      page: this.backlogPage,
      limit: this.backlogPageSize,
      where: {
        project_id: this.projectId,
        sprint_id: null,
        client_id: this.localStorage.getClientId(),
      },
      ...(this.searchBacklogQuery?.trim() ? { search: this.searchBacklogQuery.trim() } : {}),
    }).subscribe({
      next: (res: any) => {
        const rows = res?.rows || res?.data?.rows || res?.data || [];
        this.backlogTotal = res?.total || res?.data?.total || res?.count || res?.data?.count || 0;
        if (this.backlogPage === 1) {
          this.filteredBacklogStories = rows;
        } else {
          this.filteredBacklogStories = [...this.filteredBacklogStories, ...rows];
        }
        this.backlogHasMore = this.filteredBacklogStories.length < this.backlogTotal;
        this.backlogLoadingMore = false;
      },
      error: () => {
        this.backlogLoadingMore = false;
        this.toastr.error('Failed to load backlog stories');
      }
    });
  }

  loadMoreBacklogStories() {
    if (!this.backlogHasMore || this.backlogLoadingMore) return;
    this.backlogPage++;
    this.loadBacklogStories();
  }

  onBacklogSearchChange() {
    this.backlogPage = 1;
    this.filteredBacklogStories = [];
    this.loadBacklogStories();
  }

  toggleAllBacklogStories(event: any) {
    const checked = event.target.checked;
    this.filteredBacklogStories.forEach((s) => {
      this.selectedBacklogStoryIds[s.user_story_id] = checked;
    });
  }

  isAllBacklogStoriesSelected(): boolean {
    const filtered = this.filteredBacklogStories;
    if (filtered.length === 0) return false;
    return filtered.every((s) => this.selectedBacklogStoryIds[s.user_story_id]);
  }

  addSelectedStoriesToSprint() {
    const storyIds = Object.keys(this.selectedBacklogStoryIds)
      .filter((id) => this.selectedBacklogStoryIds[Number(id)])
      .map(Number);

    if (storyIds.length === 0) {
      this.toastr.warning('Please select at least one user story');
      return;
    }

    this.btnLoading = true;
    const obsList: any[] = [];
    const clientId = this.localStorage.getClientId();
    const sprintId = this.selectedSprint.sprint_id;

    storyIds.forEach((id) => {
      // 1. Update Story Sprint ID
      obsList.push(
        this.userStoryService.updateUserStory({
          user_story_id: id,
          client_id: clientId,
          sprint_id: Number(sprintId),
        }),
      );

      // Note: Tasks/bugs for the story will automatically appear in the sprint
      // when their user_story's sprint_id is updated above (server-side join).
    });

    forkJoin(obsList).subscribe({
      next: () => {
        this.toastr.success(
          'Stories and their tasks/bugs added to sprint successfully',
        );
        this.storiesUpdated.emit();
        this.sprintsUpdated.emit();
        this.modalService.dismissAll();
        this.btnLoading = false;
      },
      error: (err) => {
        this.toastr.error('Failed to add stories to sprint');
        this.btnLoading = false;
      },
    });
  }

  openCreateStoryModal(content: any) {
    this.loadProjectFeatures();
    this.newStoryData = {
      title: '',
      description: '',
      story_points: 1,
      priority: 'medium',
      status: 'todo',
      feature_id: null,
      sprint_id: this.selectedSprint.sprint_id,
      client_id: this.localStorage.getClientId(),
    };
    this.modalService.open(content, { centered: true, size: 'md' });
  }

  submitNewStory() {
    if (!this.newStoryData.title) {
      this.toastr.warning('Please enter story title');
      return;
    }
    if (!this.newStoryData.feature_id) {
      this.toastr.warning('Please select a feature');
      return;
    }
    this.btnLoading = true;
    this.userStoryService.createUserStory(this.newStoryData).subscribe({
      next: () => {
        this.toastr.success(
          'User Story created and assigned to sprint successfully',
        );
        this.storiesUpdated.emit();
        this.modalService.dismissAll();
        this.btnLoading = false;
      },
      error: (e) => {
        this.toastr.error(e?.error?.message || 'Failed to create user story');
        this.btnLoading = false;
      },
    });
  }

  openImportCsvModal(content: any) {
    this.csvText = '';
    this.loadProjectFeatures();
    this.newStoryData.feature_id = null;
    this.modalService.open(content, { centered: true, size: 'md' });
  }

  submitCsvImport() {
    if (!this.newStoryData.feature_id) {
      this.toastr.warning('Please select a feature for imported stories');
      return;
    }
    if (!this.csvText) {
      this.toastr.warning('Please enter or paste CSV content');
      return;
    }

    const lines = this.csvText
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) {
      this.toastr.warning('No valid rows found in CSV');
      return;
    }

    this.btnLoading = true;
    const obsList: any[] = [];
    const clientId = this.localStorage.getClientId();
    const sprintId = this.selectedSprint.sprint_id;
    const featureId = this.newStoryData.feature_id;

    // Header check (if first line contains title, skip it)
    let startIndex = 0;
    if (
      lines[0].toLowerCase().includes('title') ||
      lines[0].toLowerCase().includes('name')
    ) {
      startIndex = 1;
    }

    for (let i = startIndex; i < lines.length; i++) {
      const parts = lines[i].split(',').map((p) => p.trim());
      const title = parts[0];
      const points = parts[1] ? Number(parts[1]) : 1;
      const priority = parts[2] ? parts[2].toLowerCase() : 'medium';

      if (title) {
        obsList.push(
          this.userStoryService.createUserStory({
            title,
            story_points: isNaN(points) ? 1 : points,
            priority: ['low', 'medium', 'high', 'critical'].includes(priority)
              ? priority
              : 'medium',
            status: 'todo',
            feature_id: featureId,
            sprint_id: Number(sprintId),
            client_id: clientId,
          }),
        );
      }
    }

    if (obsList.length === 0) {
      this.toastr.warning('No valid user stories to import');
      this.btnLoading = false;
      return;
    }

    forkJoin(obsList).subscribe({
      next: () => {
        this.toastr.success(
          `Imported ${obsList.length} user stories successfully`,
        );
        this.storiesUpdated.emit();
        this.modalService.dismissAll();
        this.btnLoading = false;
      },
      error: () => {
        this.toastr.error('Failed to import user stories');
        this.btnLoading = false;
      },
    });
  }

  getAvatarColor(user: any): string {
    if (!user) return '#6366f1';
    const colors = [
      '#5b6af9',
      '#10b981',
      '#f59e0b',
      '#ef4444',
      '#8b5cf6',
      '#06b6d4',
      '#ec4899',
    ];
    const name = (user.fname || '') + (user.lname || '');
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }
}
